import type { QueryClient } from '@tanstack/react-query'
import { taskKeys } from '@/modules/tasks/hooks/useTasks'
import type { NotificationItem } from '@/modules/notifications/services/notification.service'

/**
 * Applies a live task.* notification. The tasks list has far more scope/filter
 * permutations than sales leads (mine/assigned/all × status/priority/assignee/
 * due date), so rather than patch a specific cached page in place, this takes
 * the simpler, always-correct route: invalidate the affected queries so the
 * next render refetches them.
 */
export function applyTaskNotification(qc: QueryClient, notification: NotificationItem) {
  if (notification.entityType !== 'task' && notification.type !== 'task.overdue_digest') return

  // Lightweight, non-persisted push (see taskService.js's notifyReceiptUpdate
  // / notifyReactionUpdate) fired when a receipt or a reaction on this
  // task's Discussion just changed — only the Discussion needs a live
  // refetch so ticks/mic badges/reaction chips catch up immediately instead
  // of waiting on the next poll.
  if (notification.type === 'task.receipt_updated' || notification.type === 'task.reaction_updated') {
    if (notification.entityId) void qc.invalidateQueries({ queryKey: taskKeys.comments(notification.entityId) })
    return
  }

  void qc.invalidateQueries({ queryKey: taskKeys.lists() })
  void qc.invalidateQueries({ queryKey: taskKeys.reminder() })

  const taskId = notification.entityId
  if (!taskId) return
  void qc.invalidateQueries({ queryKey: taskKeys.detail(taskId) })

  if (notification.type === 'task.comment_added' || notification.type === 'task.mention') {
    void qc.invalidateQueries({ queryKey: taskKeys.comments(taskId) })
  }
  void qc.invalidateQueries({ queryKey: taskKeys.activity(taskId) })
}
